import { useState } from "react";
import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import NewsletterForm from "@/pages/home/components/NewsletterForm";
import { categories, popularWorks, gemWorks, faqs } from "@/mocks/doujinshi";

function SectionHeading({
  eyebrow,
  title,
  desc,
  to,
  linkText,
}: {
  eyebrow: string;
  title: string;
  desc?: string;
  to?: string;
  linkText?: string;
}) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="text-sm font-medium uppercase tracking-widest text-accent-600">{eyebrow}</p>
        <h2 className="font-heading mt-2 text-2xl font-bold text-foreground-950 md:text-3xl">{title}</h2>
        {desc && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">{desc}</p>}
      </div>
      {to && linkText && (
        <Link
          to={to}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-primary-300 px-5 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-500 hover:text-white"
        >
          {linkText}
          <i className="ri-arrow-right-line"></i>
        </Link>
      )}
    </div>
  );
}

function FaqAccordion() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="mt-8 space-y-3">
      {faqs.slice(0, 4).map((item, i) => (
        <div key={item.q} className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
          <button
            type="button"
            onClick={() => setOpen(open === i ? null : i)}
            className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
            aria-expanded={open === i}
          >
            <span className="text-sm font-medium text-foreground-900 md:text-base">{item.q}</span>
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className={`ri-${open === i ? "subtract" : "add"}-line`}></i>
            </span>
          </button>
          {open === i && (
            <p className="px-5 pb-5 text-sm leading-relaxed text-foreground-600">{item.a}</p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Home() {
  useSeo({
    title: "同人志指南 | 同人志分类·配对推荐·热门作品与冷门佳作",
    description:
      "同人志指南是全面的同人漫画导航站，提供同人志分类、CP配对推荐、百合与耽美同人志分类，以及热门同人志榜单和冷门佳作盘点，助你快速找到想看的同人作品。",
    path: "/",
  });

  return (
    <div className="bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative flex min-h-[88vh] items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center object-top"
          style={{
            backgroundImage:
              "url(https://readdy.ai/api/search-image?query=warm%20artistic%20illustration%20of%20an%20open%20glowing%20comic%20book%20with%20floating%20illustrated%20fan%20character%20panels%2C%20sakura%20petals%20and%20ink%20splashes%2C%20coral%20rose%20and%20cream%20palette%20with%20dark%20charcoal%20depth%2C%20dreamy%20creative%20culture%20mood%2C%20high%20detail&width=1600&height=900&seq=hero-main&orientation=landscape)",
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/60"></div>
        <div className="relative mx-auto w-full max-w-7xl px-4 py-24 text-center md:px-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-1.5 text-xs font-medium text-background-50 backdrop-blur">
            <i className="ri-sparkling-2-line"></i>
            一站式同人志分类导航与作品盘点
          </span>
          <h1 className="font-heading mx-auto mt-6 max-w-4xl text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            找到每一份你热爱的
            <span className="text-accent-300">同人志</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-background-100/90 md:text-lg">
            从百合、耽美到热血、悬疑，我们为你梳理清晰的全品类分类，带来靠谱的 CP 配对推荐、热门榜单与冷门佳作盘点，让选书不再迷茫。
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/category"
              className="whitespace-nowrap rounded-full bg-primary-500 px-8 py-3.5 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              浏览同人志分类
            </Link>
            <Link
              to="/popular"
              className="whitespace-nowrap rounded-full border border-white/40 bg-white/10 px-8 py-3.5 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-white/20"
            >
              查看热门榜单
            </Link>
          </div>

          <div className="mx-auto mt-12 grid max-w-3xl grid-cols-2 gap-6 sm:grid-cols-4">
            {[
              { num: "1200+", label: "收录作品" },
              { num: "8", label: "大类分类" },
              { num: "30+", label: "热门配对" },
              { num: "500+", label: "冷门宝藏" },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl bg-white/10 px-4 py-5 backdrop-blur">
                <p className="font-heading text-2xl font-bold text-accent-300 md:text-3xl">{s.num}</p>
                <p className="mt-1 text-xs text-background-100">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="Category"
          title="同人志分类速览"
          desc="按题材划分的清晰分类，百合、耽美、纯爱、热血、仙侠……无论你是老同好还是新手入门，都能快速定位到想看的方向。"
          to="/category"
          linkText="查看全部分类"
        />
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c) => (
            <Link
              key={c.id}
              to="/category"
              className="group overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-shadow hover:shadow-md"
            >
              <div className="relative h-36 w-full overflow-hidden">
                <img
                  src={c.image}
                  alt={`${c.name}同人志分类插图`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                <span className="absolute left-3 top-3 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-white">
                  {c.count}+
                </span>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-base font-bold text-foreground-950">{c.name}</h3>
                  <span className="text-xs text-secondary-500">{c.en}</span>
                </div>
                <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">{c.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Popular works */}
      <section className="bg-background-100 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading
            eyebrow="Trending"
            title="热门同人志榜单"
            desc="近期口碑与热度双高的人气作品精选，附详细 CP 配对与题材标签，方便你快速上手入坑。"
            to="/popular"
            linkText="查看完整榜单"
          />
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {popularWorks.map((w) => (
              <Link
                key={w.id}
                to="/popular"
                className="group overflow-hidden rounded-2xl border border-background-200 bg-background-50"
              >
                <div className="relative h-64 w-full overflow-hidden">
                  <img
                    src={w.image}
                    alt={`${w.title}同人志封面`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <span className="absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-accent-500 text-sm font-bold text-white">
                    {w.rank}
                  </span>
                  <span className="absolute bottom-3 right-3 rounded-full bg-foreground-950/80 px-3 py-1 text-xs text-background-50">
                    ★ {w.rating}
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-base font-bold text-foreground-950">{w.title}</h3>
                  <p className="mt-1 text-xs text-accent-700">{w.pairing}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-secondary-500">{w.category} · {w.author}</span>
                    <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">{w.tag}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Pairing recommendation band */}
      <section className="relative overflow-hidden py-16 md:py-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://readdy.ai/api/search-image?query=soft%20warm%20abstract%20illustration%20of%20two%20overlapping%20silhouettes%20separated%20by%20a%20glowing%20line%20of%20light%20with%20floating%20petals%2C%20coral%20rose%20and%20cream%20tones%20on%20deep%20charcoal%2C%20romantic%20connection%20mood%2C%20artistic&width=1600&height=700&seq=hero-pairing&orientation=landscape)",
          }}
        ></div>
        <div className="absolute inset-0 bg-foreground-950/70"></div>
        <div className="relative mx-auto max-w-5xl px-4 text-center md:px-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-4 py-1.5 text-xs font-medium text-white">
            <i className="ri-hearts-line"></i>
            CP 配对推荐
          </span>
          <h2 className="font-heading mt-5 text-2xl font-bold text-background-50 md:text-4xl">
            从角色性格到叙事张力，帮你找到最对味的那一对
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-background-100/90 md:text-base">
            我们把人气 CP 拆解为「性格互补」「宿命纠葛」「欢喜冤家」「双向救赎」等类型，按你的喜好精准匹配，让每一份配对都能戳中你的心。
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
            {["双向救赎", "性格互补", "欢喜冤家", "宿命纠葛", "细水长流", "高山流水"].map((t) => (
              <Link
                key={t}
                to="/popular"
                className="whitespace-nowrap rounded-full border border-white/30 bg-white/10 px-4 py-2 text-sm text-background-50 backdrop-blur transition-colors hover:bg-accent-500 hover:border-accent-500"
              >
                {t}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Gems */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="Hidden Gems"
          title="冷门佳作盘点"
          desc="尚未被广泛发现却完成度极高的宝藏作品，小众但惊艳，适合想要走出舒适区、发现惊喜的你。"
          to="/gems"
          linkText="发现更多宝藏"
        />
        <div className="mt-8 grid grid-cols-2 gap-5 lg:grid-cols-4">
          {gemWorks.slice(0, 4).map((g) => (
            <Link key={g.id} to="/gems" className="group relative overflow-hidden rounded-2xl">
              <div className="relative h-72 w-full overflow-hidden">
                <img
                  src={g.image}
                  alt={`${g.title}冷门同人志作品`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute inset-x-0 bottom-0 p-4">
                <h3 className="font-heading text-base font-bold text-background-50">{g.title}</h3>
                <p className="mt-1 text-xs text-accent-300">{g.pairing} · {g.category}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Values / About */}
      <section className="bg-background-100 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <p className="text-sm font-medium uppercase tracking-widest text-accent-600">About Us</p>
              <h2 className="font-heading mt-2 text-2xl font-bold text-foreground-950 md:text-3xl">
                为热爱同人志的你，搭建一盏指路明灯
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                同人志指南由一群资深同好共同维护。我们深知「想看却不知从何看起」的困扰，于是坚持对海量作品进行细致分类、谨慎筛选与真实推荐，把优质内容以最清晰的方式呈现给你。
              </p>
              <ul className="mt-6 space-y-4">
                {[
                  { icon: "ri-focus-3-line", title: "清晰分类", desc: "全品类标签化整理，路径一目了然。" },
                  { icon: "ri-eye-line", title: "真实筛选", desc: "编辑人工把关，只推荐真正值得读的作品。" },
                  { icon: "ri-lightbulb-line", title: "持续更新", desc: "紧跟新作口碑，不断优化榜单与配对。" },
                ].map((v) => (
                  <li key={v.title} className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                      <i className={`${v.icon} text-lg`}></i>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{v.title}</p>
                      <p className="mt-0.5 text-xs text-foreground-600">{v.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <Link
                to="/about"
                className="mt-6 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-900 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-800"
              >
                了解我们更多
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                "https://readdy.ai/api/search-image?query=cozy%20illustration%20of%20a%20bookshelf%20filled%20with%20colorful%20manga%20and%20comics%20in%20a%20warm%20room%2C%20coral%20and%20cream%20tones%2C%20reading%20community%20mood%2C%20clean%20composition&width=600&height=800&seq=about-1&orientation=portrait",
                "https://readdy.ai/api/search-image?query=warm%20illustration%20of%20a%20group%20of%20friends%20reading%20and%20discussing%20comics%20around%20a%20low%20table%2C%20golden%20light%2C%20coral%20and%20cream%20palette%2C%20community%20sharing%20mood%2C%20clean%20composition&width=600&height=800&seq=about-2&orientation=portrait",
              ].map((img, i) => (
                <div key={i} className="overflow-hidden rounded-2xl">
                  <img src={img} alt={i === 0 ? "同人志书架收藏" : "同好共读交流"} className="h-72 w-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <p className="text-sm font-medium uppercase tracking-widest text-accent-600">FAQ</p>
            <h2 className="font-heading mt-2 text-2xl font-bold text-foreground-950 md:text-3xl">常见问题</h2>
            <p className="mt-3 text-sm text-foreground-600">关于同人志、分类与推荐方式，这里帮你快速解答。</p>
          </div>
          <FaqAccordion />
          <div className="mt-6 text-center">
            <Link to="/faq" className="text-sm font-medium text-primary-700 hover:text-primary-800">
              查看全部问题 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-16 md:px-6 md:pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-secondary-950 px-6 py-12 text-center md:py-16">
          <div className="absolute -left-10 -top-10 h-40 w-40 rounded-full bg-primary-500/30 blur-2xl"></div>
          <div className="absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-accent-500/30 blur-2xl"></div>
          <div className="relative">
            <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
              订阅更新，第一时间发现好作品
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-secondary-300">
              留下邮箱，我们将不定期为你送上同人志分类盘点、热门榜单与冷门佳作精选，不打扰、不刷屏。
            </p>
            <div className="flex justify-center">
              <NewsletterForm />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}